import { z } from "zod/v4";

import { Calendar } from "../../common/calendar";
import { Nutrient } from "../nutrient";

const nutritionGoalSchema = z.object({
  type: z.union([z.literal("weight_loss"), z.literal("maintenance"), z.literal("muscle_gain")]),
  energyOffsetKcal: z.number(),
});

const scheduleConfigSlotSchema = z.object({
  mealScheduleSlotId: z.uuid(),
  name: z.string().trim().min(1),
  time: z.preprocess((value) => {
    if (value instanceof Temporal.PlainTime) return value;
    if (typeof value === "string") return Temporal.PlainTime.from(value);
    return value;
  }, z.instanceof(Temporal.PlainTime)),
  sortIndex: z.string().trim().min(1),
});

const scheduleConfigAssignmentSchema = z.object({
  mealScheduleId: z.uuid(),
  dateSelector: Calendar.dateSelectorSchema,
  name: z.string().trim().min(1),
  slots: z.array(scheduleConfigSlotSchema).min(1),
});

export const mealPlanningSessionScheduleConfigDataSchema = z
  .object({ version: z.literal(1), assignments: z.array(scheduleConfigAssignmentSchema).min(1) })
  .check((ctx) => {
    Calendar.validateDateSelectors(ctx.value.assignments, ctx.issues.push);
    const scheduleIds = new Set<string>();
    for (const assignment of ctx.value.assignments) {
      if (scheduleIds.has(assignment.mealScheduleId)) {
        ctx.issues.push({
          code: "custom",
          message: "Schedule config cannot contain duplicate schedule assignments",
          input: ctx.value.assignments,
        });
      }
      scheduleIds.add(assignment.mealScheduleId);
    }
  });

export type MealPlanningSessionScheduleConfigData = z.infer<
  typeof mealPlanningSessionScheduleConfigDataSchema
>;
const includedSlotTargetSchema = z.object({
  mealScheduleId: z.uuid(),
  mealScheduleSlotId: z.uuid(),
  nutritionProgramDayId: z.uuid().nullable(),
  status: z.literal("included"),
  nutrientTargets: Nutrient.targetsSchema({ requiredKeys: Nutrient.macroEnergyKeys }),
});

const skippedSlotTargetSchema = z.object({
  mealScheduleId: z.uuid(),
  mealScheduleSlotId: z.uuid(),
  nutritionProgramDayId: z.uuid().nullable(),
  status: z.literal("skipped"),
});

const slotTargetSchema = z.discriminatedUnion("status", [
  includedSlotTargetSchema,
  skippedSlotTargetSchema,
]);

export const mealPlanningSessionMemberContextDataSchema = z.object({
  nutritionGoal: nutritionGoalSchema.nullable(),
  preferences: z.array(z.string()),
  slotTargets: z.array(slotTargetSchema),
});

export type MealPlanningSessionMemberContextData = z.infer<
  typeof mealPlanningSessionMemberContextDataSchema
>;
